import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { BellOff, BellRing, Save, RefreshCw, FlaskConical, MessageSquareText, SendHorizonal, History, Settings2, ShieldCheck, UserCog, Users, Zap, Hand } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Linkify } from "../../components/Linkify";
import { SendMessagesPanel } from "../../components/messages/SendMessagesPanel";   // Phase 227
import { api, errMsg, fmtDate, fmtDateTime } from "../../lib/api";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice } from "../../components/FeatureGate";

const LANGUAGES = [
  { code: "en", name: "English" }, { code: "hi", name: "Hindi" }, { code: "as", name: "Assamese" },
  { code: "bn", name: "Bengali" }, { code: "brx", name: "Bodo" }, { code: "doi", name: "Dogri" },
  { code: "gu", name: "Gujarati" }, { code: "kn", name: "Kannada" }, { code: "ks", name: "Kashmiri" },
  { code: "kok", name: "Konkani" }, { code: "mai", name: "Maithili" }, { code: "ml", name: "Malayalam" },
  { code: "mni", name: "Manipuri" }, { code: "mr", name: "Marathi" }, { code: "ne", name: "Nepali" },
  { code: "or", name: "Odia" }, { code: "pa", name: "Punjabi" }, { code: "sa", name: "Sanskrit" },
  { code: "sat", name: "Santali" }, { code: "sd", name: "Sindhi" }, { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" }, { code: "ur", name: "Urdu" },
];
// Phase 227: Automatic / Manual per message type and the citizen / staff switches are decided by the Platform Super
// Admin (Tenant Configuration → Messages). The only switch the Municipal Admin keeps here is the "Demand corrected"
// system alert opt-out, exactly as before.
const EVENT_LABELS = {
  demand_corrected: "Demand corrected",
};
// labels for the log (every event that can appear in the outbox)
const LOG_EVENT_LABELS = {
  ...EVENT_LABELS,
  demand_created: "New demand generated",
  payment_received: "Payment received",
  partial_payment: "Partial payment received",
  citizen_portal_payment: "Citizen Portal payment confirmation",
  payment_due_reminder: "Property Tax Due",
  pending_payment_notice: "Pending Payment (User Charge)",
  final_notice: "Final Notice",
  cheque_bounced: "Cheque bounced",
  visit_update: "Visit update",
  password_reset: "Staff password reset",
  password_reset_otp: "Staff password reset code",
};
const STATUS_VARIANT = { sent: "default", simulated: "secondary", failed: "destructive", skipped: "outline", suppressed: "outline" };
// Phase 189: one-line explanation under events whose content is not obvious from the name
const EVENT_HINTS = {
  demand_corrected: "Sent to the owner when an admin corrects a demand — PIN, tax head, old → new demand, paid so far, new balance or credit held (the reason is never sent). Also shown in the Citizen Portal bell.",
};
const SKIP_PRESETS = [0, 3, 7, 15, 30];
const TABS = ["settings", "send", "log"];
const tabFromHash = () => { const h = window.location.hash.replace("#", ""); return TABS.includes(h) ? h : "settings"; };

/** Phase 227: read-only summary of what the Platform Super Admin decided for this municipality. */
function MessageSettingsSummary({ ms }) {
  if (!ms) return null;
  const manual = (ms.rows || []).filter((r) => r.mode === "manual");
  return (
    <Card className={`shadow-sm ${ms.citizen_enabled ? "" : "border-destructive/50"}`} data-testid="message-settings-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Settings2 className="h-4 w-4" /> Message settings for this municipality</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Decided by the Platform Super Admin (Tenant Configuration → Messages) and shown here for reference. Ask the Super Admin to change them.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={ms.citizen_enabled ? "secondary" : "destructive"} className="gap-1" data-testid="ms-citizen-badge">
              {ms.citizen_enabled ? <Users className="h-3 w-3" /> : <BellOff className="h-3 w-3" />} Citizen messages {ms.citizen_enabled ? "On" : "Off"}
            </Badge>
            <Badge variant={ms.staff_enabled ? "secondary" : "destructive"} className="gap-1" data-testid="ms-staff-badge">
              {ms.staff_enabled ? <UserCog className="h-3 w-3" /> : <BellOff className="h-3 w-3" />} Staff messages {ms.staff_enabled ? "On" : "Off"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!ms.citizen_enabled && (
          <p className="text-xs text-destructive flex items-center gap-1.5 border border-destructive/30 rounded-md px-3 py-2" data-testid="ms-citizen-off-note">
            <BellOff className="h-3.5 w-3.5" /> No citizen message is sent for this municipality right now — automatic or manual. Every attempt is listed as <span className="font-medium">Skipped</span> in Notification History.
          </p>
        )}
        {!ms.staff_enabled && (
          <p className="text-xs text-destructive flex items-center gap-1.5 border border-destructive/30 rounded-md px-3 py-2" data-testid="ms-staff-off-note">
            <BellOff className="h-3.5 w-3.5" /> Staff messages are off — forgot-password links / codes and staff alerts are not sent; staff are told to contact you.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ms-modes-grid">
          {(ms.rows || []).map((r) => (
            <div key={r.event} className="flex items-start justify-between gap-2 rounded-md border px-3 py-2" data-testid={`ms-row-${r.event}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{r.label}</p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2" title={r.hint}>{r.hint}</p>
              </div>
              <Badge variant={r.mode === "manual" ? "outline" : "secondary"} className="shrink-0 gap-1 text-[10px]" data-testid={`ms-mode-${r.event}`}>
                {r.mode === "manual" ? <Hand className="h-3 w-3" /> : <Zap className="h-3 w-3" />} {r.mode === "manual" ? (r.fixed ? "Always manual" : "Manual") : (r.fixed ? "Always automatic" : "Automatic")}
              </Badge>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid="ms-footer">
          <ShieldCheck className="h-3 w-3" />
          {manual.length} type{manual.length === 1 ? "" : "s"} sent by hand from the Send Messages tab · message texts are the Templates and are unchanged
          {ms.updated_at ? ` · last changed ${fmtDateTime(ms.updated_at)}${ms.updated_by?.name ? ` by ${ms.updated_by.name}` : ""}` : ""}
        </p>
      </CardContent>
    </Card>
  );
}

export default function NotificationsAdmin() {
  const { code } = useParams();
  // Phase 152: sub-features — preferences (events / channels / languages), outbox (log), reminders (Send Messages)
  const access = useRoleModules(code);
  const showPrefs = access.featureAllowed("notifications", "preferences");
  const prefsRO = !access.canWrite("notifications", "preferences");
  const showOutbox = access.featureAllowed("notifications", "outbox");
  const showSend = access.featureAllowed("notifications", "reminders");
  const canSend = access.canWrite("notifications", "reminders");
  const [tab, setTab] = useState(tabFromHash);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outbox, setOutbox] = useState(null);
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [customDays, setCustomDays] = useState("");

  const loadPrefs = useCallback(() => {
    api.get(`/t/${code}/notification-prefs`).then((r) => setPrefs(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  // keep the active tab in the URL hash so links like …/notifications#send open the right tab
  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const changeTab = (v) => {
    setTab(v);
    if (window.location.hash !== `#${v}`) window.history.replaceState(null, "", `#${v}`);
    if (v === "log") loadOutbox();
    if (v === "settings") loadPrefs();
  };

  const loadOutbox = useCallback(() => {
    if (!showOutbox) { setOutbox({ items: [], total: 0 }); return; }
    const params = { limit: 100 };
    if (filterChannel !== "all") params.channel = filterChannel;
    if (filterEvent !== "all") params.event = filterEvent;
    if (filterStatus !== "all") params.status = filterStatus;
    api.get(`/t/${code}/notifications-outbox`, { params }).then((r) => setOutbox(r.data)).catch(() => setOutbox({ items: [], total: 0 }));
  }, [code, filterChannel, filterEvent, filterStatus, showOutbox]);
  useEffect(() => { loadOutbox(); }, [loadOutbox]);

  const savePrefs = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/notification-prefs`, {
        events: prefs.events, channels: prefs.channels, languages: prefs.languages,
        notice_skip_days: Number.isFinite(Number(prefs.notice_skip_days)) ? Number(prefs.notice_skip_days) : 7,
      });
      setPrefs((p) => ({ ...p, ...r.data }));
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleLang = (lc) => setPrefs((p) => {
    const has = p.languages.includes(lc);
    const next = has ? p.languages.filter((x) => x !== lc) : [...p.languages, lc];
    return { ...p, languages: next.length ? next : ["hi", "en"] };
  });

  if (!prefs) return <Spinner />;
  const skipDays = Number(prefs.notice_skip_days ?? 7);
  const presetValue = SKIP_PRESETS.includes(skipDays) && customDays === "" ? String(skipDays) : "custom";
  const setSkipDays = (n) => setPrefs((p) => ({ ...p, notice_skip_days: Math.max(0, Math.min(90, Math.round(n))) }));
  const ms = prefs.message_settings;
  const manualCount = (ms?.rows || []).filter((r) => r.mode === "manual").length;

  return (
    <div className="space-y-4" data-testid="notifications-admin-page">
      <PageHeader title="Citizen Notifications"
        subtitle="WhatsApp, SMS and Email alerts for demands, payments, visits and reminders — automatic ones go out by themselves, the rest you send from the Send Messages tab" />

      {prefs.simulation_mode && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="p-3 flex items-center gap-3">
            <FlaskConical className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-800">
              <span className="font-semibold">Simulation mode</span> — alerts are composed and logged but not actually sent.
              The Platform Super Admin can add provider keys (Twilio / SendGrid / SMTP / custom) to switch on live sending.
            </p>
          </CardContent>
        </Card>
      )}

      {access.loaded && !showPrefs && !showOutbox && !showSend && <NoAccessPanel what="Citizen Notifications" testId="notifications-no-access" />}

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList data-testid="notifications-tabs">
          <TabsTrigger value="settings" data-testid="tab-notif-settings"><Settings2 className="h-4 w-4 mr-1.5" /> Settings</TabsTrigger>
          {showSend && (
            <TabsTrigger value="send" data-testid="tab-notif-send">
              <SendHorizonal className="h-4 w-4 mr-1.5" /> Send Messages
              {manualCount > 0 && ms?.citizen_enabled && <Badge variant="secondary" className="ml-1.5 text-[10px]" data-testid="tab-notif-send-count">{manualCount}</Badge>}
            </TabsTrigger>
          )}
          {showOutbox && <TabsTrigger value="log" data-testid="tab-notif-log"><History className="h-4 w-4 mr-1.5" /> Notification log</TabsTrigger>}
        </TabsList>

        {/* ---------------- SETTINGS ---------------- */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          {/* Phase 227: what the Super Admin decided (read-only) */}
          <MessageSettingsSummary ms={ms} />

          {showPrefs && <fieldset disabled={prefsRO} className="space-y-4 min-w-0" data-testid="notification-prefs-fieldset">
          {prefsRO && <ReadOnlyNotice what="notification preferences" testId="notification-prefs-readonly-notice" />}

          {/* Phase 221 / 227: manual sends + municipality default for the skip-days rule */}
          <Card className="shadow-sm" data-testid="manual-sending-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading flex items-center gap-2"><MessageSquareText className="h-4 w-4" /> Sending by hand</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Dues notices (Pending Payment, Final Notice, Property Tax Due) are never automatic, and every message type the Super Admin set to <span className="font-medium text-foreground">Manual</span> waits for you.
                Open <span className="font-medium text-foreground">Send Messages</span>, choose the message, the Mohalla / Ward / Zone / whole municipality or specific properties, the channels, preview the list and confirm.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3 flex-wrap" data-testid="manual-notices-info">
                <div className="space-y-1">
                  <Label className="text-xs">Skip properties notified in the last…</Label>
                  <div className="flex items-center gap-2">
                    <Select value={presetValue} onValueChange={(v) => { if (v === "custom") { setCustomDays(String(skipDays)); } else { setCustomDays(""); setSkipDays(Number(v)); } }}>
                      <SelectTrigger className="h-9 w-40" data-testid="notice-skip-days-select"><SelectValue>{presetValue === "custom" ? "Custom…" : skipDays === 0 ? "Don't skip (0)" : `${skipDays} days`}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {SKIP_PRESETS.map((d) => <SelectItem key={d} value={String(d)} data-testid={`notice-skip-days-${d}`}>{d === 0 ? "Don't skip (0)" : `${d} days`}</SelectItem>)}
                        <SelectItem value="custom" data-testid="notice-skip-days-custom">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {presetValue === "custom" && (
                      <Input type="number" min={0} max={90} className="h-9 w-24" value={customDays}
                        onChange={(e) => { setCustomDays(e.target.value); const n = Number(e.target.value); if (Number.isFinite(n)) setSkipDays(n); }}
                        data-testid="notice-skip-days-input" />
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground" data-testid="notice-skip-days-help">Municipality default for dues notices (0 = never skip, up to 90). You can still change it for one send before pressing Send.</p>
                </div>
                {showSend && (
                  <Button type="button" variant="outline" size="sm" onClick={() => changeTab("send")} data-testid="open-send-notices-button">
                    <SendHorizonal className="h-4 w-4 mr-1.5" /> Open Send Messages
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Alert events</CardTitle>
                <p className="text-xs text-muted-foreground">System alerts you may switch off for this municipality. Whether the other types are automatic or manual is shown in Message settings above.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.keys(EVENT_LABELS).map((ev) => (
                  <div key={ev} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <Label className="text-sm">{EVENT_LABELS[ev]}</Label>
                      {EVENT_HINTS[ev] && <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`event-hint-${ev}`}>{EVENT_HINTS[ev]}</p>}
                    </div>
                    <Switch checked={!!prefs.events[ev]} onCheckedChange={(v) => setPrefs((p) => ({ ...p, events: { ...p.events, [ev]: v } }))}
                      data-testid={`event-switch-${ev}`} />
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2 opacity-80">
                  <div className="min-w-0">
                    <Label className="text-sm">Cheque bounced</Label>
                    <p className="text-[11px] text-muted-foreground leading-snug">Always sent while citizen messages are On.</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">Always on</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Channels</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[["sms", "SMS"], ["whatsapp", "WhatsApp"], ["email", "Email"]].map(([ch, label]) => (
                  <div key={ch} className="flex items-center justify-between gap-2">
                    <Label className="text-sm">{label}</Label>
                    <Switch checked={!!prefs.channels[ch]} onCheckedChange={(v) => setPrefs((p) => ({ ...p, channels: { ...p.channels, [ch]: v } }))}
                      data-testid={`channel-switch-${ch}`} />
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">Alerts go to the mobile/email saved on each property record.</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Message languages</CardTitle>
                <p className="text-xs text-muted-foreground">English + 22 scheduled languages. WhatsApp and email combine the selected languages; SMS always uses the DLT-approved text.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="language-grid">
                  {LANGUAGES.map((l) => (
                    <label key={l.code} className="flex items-center gap-2 text-xs cursor-pointer rounded px-1.5 py-1 hover:bg-muted">
                      <Checkbox checked={prefs.languages.includes(l.code)} onCheckedChange={() => toggleLang(l.code)}
                        data-testid={`lang-checkbox-${l.code}`} />
                      {l.name}
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          </fieldset>}

          <div className="flex gap-2 flex-wrap">
            {showPrefs && !prefsRO && <Button onClick={savePrefs} disabled={busy} data-testid="save-prefs-button">
              <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : "Save preferences"}
            </Button>}
          </div>
        </TabsContent>

        {/* ---------------- SEND MESSAGES (Phase 227) ---------------- */}
        {showSend && (
          <TabsContent value="send" className="mt-4 space-y-4" data-testid="notifications-send-tab">
            {!canSend && <ReadOnlyNotice what="sending messages" testId="send-messages-readonly-notice" />}
            <fieldset disabled={!canSend} className="min-w-0">
              <SendMessagesPanel key={code} code={code} canEditDefault={!prefsRO} />
            </fieldset>
          </TabsContent>
        )}

        {/* ---------------- LOG ---------------- */}
        {showOutbox && (
          <TabsContent value="log" className="mt-4">
            <Card className="shadow-sm" data-testid="notification-outbox-card">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 flex-wrap gap-2">
                <div>
                  <CardTitle className="text-base font-heading">Notification log ({outbox?.total ?? 0})</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Latest 100 entries. <span className="font-medium text-foreground">Skipped</span> = deliberately not sent (municipality switched off or type set to manual). The full register with filters and export is under Notification History.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={filterStatus} onValueChange={(v) => v && setFilterStatus(v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="outbox-status-filter">
                      <SelectValue>{filterStatus === "all" ? "All statuses" : filterStatus}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {["sent", "delivered", "simulated", "suppressed", "failed", "skipped"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterChannel} onValueChange={(v) => v && setFilterChannel(v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="outbox-channel-filter">
                      <SelectValue>{filterChannel === "all" ? "All channels" : filterChannel}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All channels</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterEvent} onValueChange={(v) => v && setFilterEvent(v)}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="outbox-event-filter">
                      <SelectValue>{filterEvent === "all" ? "All events" : LOG_EVENT_LABELS[filterEvent]}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      {Object.keys(LOG_EVENT_LABELS).map((ev) => <SelectItem key={ev} value={ev}>{LOG_EVENT_LABELS[ev]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={loadOutbox} data-testid="outbox-refresh-button">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!outbox ? <Spinner /> : outbox.items.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="outbox-empty">
                    No notifications yet — they will appear here when demands are created, payments are received or messages are sent.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Time</TableHead>
                          <TableHead className="text-xs">Event</TableHead>
                          <TableHead className="text-xs">Channel</TableHead>
                          <TableHead className="text-xs">To</TableHead>
                          <TableHead className="text-xs">PIN</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {outbox.items.map((it) => (
                          <TableRow key={it.id} data-testid="outbox-row" className={it.status === "skipped" ? "opacity-75" : ""}>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(it.created_at)}</TableCell>
                            <TableCell className="text-xs">{LOG_EVENT_LABELS[it.event] || it.event}</TableCell>
                            <TableCell className="text-xs uppercase">{it.channel}</TableCell>
                            <TableCell className="text-xs font-mono">{it.to || "—"}</TableCell>
                            <TableCell className="text-xs font-mono">{it.pin || "—"}</TableCell>
                            <TableCell>
                              <Badge variant={STATUS_VARIANT[it.status] || "secondary"} className={`text-[10px] ${it.status === "skipped" ? "border-dashed" : ""}`} data-testid={`outbox-status-${it.status}`}>{it.status}</Badge>
                            </TableCell>
                            <TableCell className="text-xs max-w-md">
                              <span className="line-clamp-2 whitespace-pre-wrap" title={it.message}><Linkify text={it.message} /></span>
                              {it.error && <span className={`block text-[10px] mt-0.5 ${it.status === "skipped" ? "text-muted-foreground" : "text-destructive"}`}>{it.error}</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
